import { generatePath, matchPath } from 'react-router-dom'
import { REPORTING_DOCUMENT_TYPES } from '@common/constants/client'
import { getClientLifecycleStage } from '@common/helpers/clientLifecycle'
import { IOngoingReporting } from '@common/interfaces/bbc'
import { IClientInfo } from '@common/interfaces/client'
import { IMasterCollection } from '@common/interfaces/collection'
import { IFee } from '@common/interfaces/loanServicing'
import { INoteMapping } from '@common/interfaces/notes'
import {
  ISearchCompany,
  ISearchResult,
  ISearchResultItem,
  ISearchSku,
  SearchType,
  asSearchType,
} from '@common/interfaces/search'
import { IUser, UserRole } from '@common/interfaces/user'
import orderBy from 'lodash/orderBy'
import partition from 'lodash/partition'
import queryString from 'query-string'
import { ClientPageTabNames, ROUTES } from '../../constants/routes'
import { databaseDateFormat } from '../../helpers/helpers'

const SECTION_KEYWORDS: Partial<Record<SearchType, string[]>> = {
  [SearchType.Collection]: ['collection', 'wire', 'check'],
  [SearchType.Fee]: ['fee', 'interest', 'minimum'],
  [SearchType.OngoingReporting]: ['financials', 'sales', 'bank', 'financial', 'general', 'ledger'],
  [SearchType.User]: ['user', 'email', 'contact'],
  [SearchType.Entity]: ['entity', 'debtor', 'customer', 'vendor'],
  [SearchType.Funding]: ['bbc', 'funding', 'borrowing'],
  [SearchType.Note]: ['note'],
  [SearchType.Invoice]: ['invoice', 'ar', 'receivable'],
  [SearchType.Task]: ['task', 'tasks', 'todo', 'to-do'],
  [SearchType.Sku]: ['sku', 'skus', 'inventory'],
}

// Opens the client's Inventory tab with Sales by SKU narrowed to the SKU. The empty quick filter
// overrides the table's "Finished Goods" default, which would hide a SKU of any other type.
export const buildSkuSearchLink = ({ clientId, sku }: Pick<ISearchSku, 'clientId' | 'sku'>) =>
  `${generatePath(ROUTES.CLIENT_PAGE, { id: clientId })}?${queryString.stringify({
    tab: ClientPageTabNames.Inventory,
    skuVisualizationFilters: queryString.stringify({ fullSearch: sku }),
    skuVisualizationQuickFilter: '',
  })}`

// Whole-word match, so "checking" doesn't count as the Collection keyword "check".
const isSectionNamedInSearchTerm = (section: SearchType, searchTerm: string) => {
  const words = searchTerm.toLowerCase().split(' ')
  return !!SECTION_KEYWORDS[section]?.some((keyword: string) => words.includes(keyword))
}

const hasHits = (results: ISearchResult[], section: SearchType) =>
  !!results.find(({ type }) => type === section)?.items?.length

// Task takes precedence when a query names more than one section ("funding tasks"),
// so the keyword override lands on the same bucket whose demotion it disables — but
// only when tasks actually matched, otherwise the query's other section wins.
// Everything else resolves to the first match in key order.
const getSectionFromSearchTerm = (results: ISearchResult[], searchTerm: string) => {
  if (
    isSectionNamedInSearchTerm(SearchType.Task, searchTerm) &&
    hasHits(results, SearchType.Task)
  ) {
    return SearchType.Task
  }
  return Object.keys(SECTION_KEYWORDS)
    .map(asSearchType)
    .find((section) => !!section && isSectionNamedInSearchTerm(section, searchTerm))
}

const moveSectionToTop = (results: ISearchResult[], section: SearchType) => {
  const [sectionResults, otherResults] = partition(results, ({ type }) => type === section)
  return [...sectionResults, ...otherResults]
}

const moveSectionToBottom = (results: ISearchResult[], section: SearchType) => {
  const [sectionResults, otherResults] = partition(results, ({ type }) => type === section)
  return [...otherResults, ...sectionResults]
}

// A bucket's maxScore is an OpenSearch `_score`, only meaningful within its own
// index — so "YSE" lets the Task bucket outscore the one company that IS YSE Beauty,
// because dozens of task titles embed the name ("Funding - YSE Beauty - 09/14/2026").
// Tasks are context about a client, not the client, so they sort last unless the
// query names them. Nothing is dropped; the full results page is unaffected.
const demoteTaskResults = (results: ISearchResult[], searchTerm: string) =>
  isSectionNamedInSearchTerm(SearchType.Task, searchTerm)
    ? results
    : moveSectionToBottom(results, SearchType.Task)

// The client family now lives in the single Company bucket. When the Company
// bucket's top hit is a client (client-linked companies are score-boosted above
// vendor/customer companies server-side), promote the whole bucket to the top so
// a client-name match is the first result. If the Company bucket only matched
// non-client companies, leave the maxScore ordering untouched.
const promoteCompanyResultsToTop = (results: ISearchResult[]) => {
  const company = results.find((result) => result.type === SearchType.Company)
  const topItem = company?.items?.[0]?.item as ISearchCompany | undefined
  if (!topItem?.isClient) {
    return results
  }
  return moveSectionToTop(results, SearchType.Company)
}

const organizeResultsBySectionBasedOnSearchTerm = (
  results: ISearchResult[],
  searchTerm: string,
) => {
  const section = getSectionFromSearchTerm(results, searchTerm)
  if (!section) {
    return results
  }
  return moveSectionToTop(results, section)
}

/**
 * Section order for the header typeahead, strongest rule last: maxScore only
 * breaks ties between buckets, tasks sink, a client-linked Company bucket floats,
 * and naming a section outright ("fees", "tasks") beats all of it.
 */
const orderSearchResultSections = (results: ISearchResult[], searchTerm: string) => {
  const byScore = orderBy(results, 'maxScore', 'desc')
  const withTasksDemoted = demoteTaskResults(byScore, searchTerm)
  const withCompanyPromoted = promoteCompanyResultsToTop(withTasksDemoted)
  return organizeResultsBySectionBasedOnSearchTerm(withCompanyPromoted, searchTerm)
}

// `pathname`/`currentSearch` are the caller's current location, passed only by the
// header dropdown — they let a task result open in place when the page already
// hosts the task panel. Callers that omit them get the plain destination link.
const generateSearchResultItemLink = (
  data: ISearchResultItem,
  isBDO = false,
  pathname?: string,
  currentSearch?: string,
) => {
  let link: string
  switch (data.type) {
    case SearchType.Client:
      link = generatePath(ROUTES.CLIENT_PAGE, { id: data.id })
      break
    case SearchType.DueDiligence:
      link = generatePath(ROUTES.DUE_DILIGENCE, { id: data.id })
      break
    case SearchType.Prospect: {
      const clientInfo = data.item as IClientInfo
      link = clientInfo.firstOPSReportingId
        ? generatePath(ROUTES.PROSPECT_PAGE, {
            id: clientInfo.firstOPSReportingId,
          })
        : null
      break
    }
    case SearchType.Funding: {
      link = generatePath(ROUTES.BBC_SUMMARY, { id: data.id })
      break
    }
    case SearchType.Collection: {
      const collection = data.item as IMasterCollection

      link = `${generatePath(ROUTES.CLIENT_PAGE, {
        id: collection.clientId,
      })}?${queryString.stringify({
        accountActivityFilters: queryString.stringify(
          {
            amountMax: collection.paymentAmount,
            amountMin: collection.paymentAmount,
            recordDateFrom: databaseDateFormat(collection.recordDate),
            recordDateTo: databaseDateFormat(collection.recordDate),
            type: collection.paymentType,
          },
          { arrayFormat: 'index' },
        ),
      })}`
      break
    }
    case SearchType.Fee: {
      const fee = data.item as IFee

      link = `${generatePath(ROUTES.CLIENT_PAGE, {
        id: fee.clientId,
      })}?${queryString.stringify({
        accountActivityFilters: queryString.stringify({
          amountMax: fee.amount,
          amountMin: fee.amount,
          recordDateFrom: databaseDateFormat(fee.recordDate),
          recordDateTo: databaseDateFormat(fee.recordDate),
        }),
      })}`
      break
    }
    case SearchType.Note: {
      const note = data.item as INoteMapping
      const noteId = note.noteParentId || note.id

      link = `${
        note.borrowingBaseId
          ? generatePath(ROUTES.BBC_SUMMARY, { id: note.borrowingBaseId })
          : note.ongoingReportingId
            ? generatePath(ROUTES.ONGOING_REPORTING, {
                id: note.ongoingReportingId,
              })
            : note.opsReportingId
              ? generatePath(ROUTES.PROSPECT_PAGE, { id: note.opsReportingId })
              : note.clientId
                ? generatePath(ROUTES.CLIENT_PAGE, { id: note.clientId })
                : ''
      }?${queryString.stringify({
        noteId,
        notesContainerFilters: queryString.stringify(
          {
            id: [noteId],
          },
          { arrayFormat: 'index' },
        ),
      })}`
      break
    }
    case SearchType.OngoingReporting: {
      const reporting = data.item as IOngoingReporting

      if (
        [
          REPORTING_DOCUMENT_TYPES.financials,
          REPORTING_DOCUMENT_TYPES.financialProjections,
        ].includes(reporting.type)
      ) {
        link = generatePath(ROUTES.ONGOING_REPORTING, { id: reporting.id })
      } else if (REPORTING_DOCUMENT_TYPES.salesBySKU === String(reporting.type)) {
        link = generatePath(ROUTES.ONGOING_REPORTING_SALES_BY_SKU, {
          id: reporting.id,
        })
      } else if (REPORTING_DOCUMENT_TYPES.arGeneralLedger === String(reporting.type)) {
        link = generatePath(ROUTES.ONGOING_REPORTING_AR_GENERAL_LEDGER, {
          id: reporting.id,
        })
      } else if (REPORTING_DOCUMENT_TYPES.bankTransactions === String(reporting.type)) {
        link = generatePath(ROUTES.ONGOING_REPORTING_BANK_TRANSACTIONS_UPLOAD, {
          id: reporting.id,
        })
      } else if (REPORTING_DOCUMENT_TYPES.capTable === String(reporting.type)) {
        link = generatePath(ROUTES.ONGOING_REPORTING_CAP_TABLE_UPLOAD, {
          id: reporting.id,
        })
      }
      break
    }
    case SearchType.User: {
      const user = data.item as IUser

      link =
        [UserRole.CLIENT_USER].includes(user.role) && user.clientId
          ? generatePath(ROUTES.CLIENT_SETUP_USERS, { id: user.clientId })
          : ROUTES.USER_MANAGEMENT
      break
    }
    case SearchType.Entity: {
      link = generatePath(ROUTES.ENTITY_PAGE, { id: data.id })
      break
    }
    // CRM results route to the legacy page when a linked legacy record exists
    // (views are being unified), else to the CRM detail page.
    case SearchType.Organization: {
      link = generatePath(ROUTES.ORGANIZATION_PAGE, { id: data.id })
      break
    }
    case SearchType.Company: {
      const company = data.item as ISearchCompany
      // BDO users can't open the loan-servicing lifecycle pages — their company
      // view is always the CRM company page (which renders the Overview in place
      // for them). So route every company result there, regardless of stage.
      if (isBDO) {
        link = generatePath(ROUTES.CRM_COMPANY_PAGE, { id: data.id })
        break
      }
      // Route a client-linked company to its lifecycle page (client loan / due
      // diligence / prospect), mirroring the legacy Client-family routing. Pure
      // CRM companies (and prospects without an OPS reporting record) go to the
      // CRM company page.
      const stage = company.clientInfoId ? getClientLifecycleStage(company.clientStatus) : null
      if (stage === 'client' && company.clientInfoId) {
        link = generatePath(ROUTES.CLIENT_PAGE, { id: company.clientInfoId })
      } else if (stage === 'dueDiligence' && company.clientInfoId) {
        link = generatePath(ROUTES.DUE_DILIGENCE, { id: company.clientInfoId })
      } else if (stage === 'prospect' && company.firstOPSReportingId) {
        link = generatePath(ROUTES.PROSPECT_PAGE, { id: company.firstOPSReportingId })
      } else {
        link = generatePath(ROUTES.CRM_COMPANY_PAGE, { id: data.id })
      }
      break
    }
    case SearchType.Person: {
      // Always open the person's CRM profile — even when they have a linked Atlas
      // account (userId). The profile surfaces the Atlas-account details inline, so
      // there's no reason to bounce a person result to the flat user-management list.
      link = generatePath(ROUTES.CRM_PERSON_PAGE, { id: data.id })
      break
    }
    case SearchType.Participant: {
      link = generatePath(ROUTES.PARTICIPANTS_PAGE, { id: data.id })
      break
    }
    case SearchType.Invoice: {
      // Invoices now use modal instead of redirect
      link = ''
      break
    }
    case SearchType.Sku: {
      link = buildSkuSearchLink(data.item as ISearchSku)
      break
    }
    case SearchType.Task: {
      // `taskId` opens the detail drawer, the same deep link notifications use (see
      // common/helpers/notificationRouting). The drawer fetches the task by id, so
      // this works for another user's task too even though the list underneath only
      // shows your own.
      //
      // My Tasks and the project page both host that drawer (they share
      // useTaskPanelSelection), so searching from either opens the task in place and
      // keeps the page's existing filters, rather than jumping to My Tasks and
      // resetting the query string. From anywhere else, My Tasks is the only host we
      // can send them to.
      const isPanelHost =
        !!pathname &&
        (!!matchPath(ROUTES.MY_TASKS, pathname) || !!matchPath(ROUTES.TASK_PROJECT_PAGE, pathname))

      link = isPanelHost
        ? `${pathname}?${queryString.stringify({
            ...queryString.parse(currentSearch || ''),
            taskId: data.id,
          })}`
        : `${ROUTES.MY_TASKS}?${queryString.stringify({ taskId: data.id })}`
      break
    }
    default:
      link = ''
  }

  return link
}

export { orderSearchResultSections, generateSearchResultItemLink }
